/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Label } from '../ui/Label';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { CustomSelect } from '../ui/Select';
import { LogoUpload } from './LogoUpload';
import { CompanyService } from '../../services/company.service';
import { CompanyType } from '../../types/CompanyType';
import { useAuth } from '../../context/AuthContext';
import { AuthService } from '../../services/auth.service';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (company: CompanyType) => void;
  editingCompany?: CompanyType | null;
};

export const CompanyModal = ({ isOpen, onClose, onSuccess, editingCompany }: Props) => {
  const [name, setName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [countryId, setCountryId] = useState('');
  const [countries, setCountries] = useState<{ label: string; value: string }[]>([]);
  const [errors, setErrors] = useState({ name: false, country: false, duplicateName: false });
  const { user, setUser } = useAuth();

  const isEdit = Boolean(editingCompany);

  const [originalState, setOriginalState] = useState({ name: '', countryId: '' });

  const isEdited =
    name !== originalState.name ||
    countryId !== originalState.countryId ||
    !!logoFile;

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const hasError = !trimmedName || !countryId;

    setErrors({
      name: !trimmedName,
      country: !countryId,
      duplicateName: false,
    });

    if (hasError) return;

    const capitalizedName = trimmedName.charAt(0).toUpperCase() + trimmedName.slice(1).toLowerCase();

    try {
      if (
        user?.role?.name === 'SUPER_ADMIN' &&
        (!isEdit || capitalizedName !== editingCompany?.name)
      ) {
        const isDuplicate = await CompanyService.checkDuplicateCompanyName(capitalizedName);
        if (isDuplicate) {
          setErrors((prev) => ({ ...prev, duplicateName: true }));
          return;
        }
      }

      const formData = new FormData();
      formData.append('name', capitalizedName);
      formData.append('countryId', countryId);
      if (logoFile) formData.append('logo', logoFile);

      const res = isEdit
        ? await CompanyService.updateCompany(editingCompany!.id, formData)
        : await CompanyService.createCompany(formData);

      onSuccess(res.data);

      if (isEdit) {
        const refreshed = await AuthService.getMe();
        setUser(refreshed.data);
      }

      onClose();
    } catch (err) {
      console.error('Failed to save company:', err);
    }
  };

  const resetForm = () => {
    setName('');
    setCountryId('');
    setLogoFile(null);
    setErrors({ name: false, country: false, duplicateName: false });
    setOriginalState({ name: '', countryId: '' });
  };

  useEffect(() => {
    if (isOpen) {
      CompanyService.getCountries()
        .then((res) => {
          const options = res.data.map((c: any) => ({
            label: `${c.name} (${c.code})`,
            value: c.id,
          }));
          setCountries(options);
        })
        .catch(console.error);

      if (isEdit && editingCompany) {
        const currentName = editingCompany.name;
        const currentCountry =
          typeof editingCompany.country === 'string'
            ? editingCompany.country
            : editingCompany.country?.id || '';

        setName(currentName);
        setCountryId(currentCountry);
        setOriginalState({
          name: currentName,
          countryId: currentCountry,
        });
      }
    } else {
      resetForm();
    }
  }, [isOpen, editingCompany]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Company' : 'Add a New Company'}>
      <Label className="mt-5 mb-1 text-lg font-semibold">Name</Label>
      <Input
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          if ((errors.name || errors.duplicateName) && e.target.value.trim()) {
            setErrors((prev) => ({ ...prev, name: false, duplicateName: false }));
          }
        }}
        placeholder="Enter name"
        error={errors.name || errors.duplicateName}
        disabled={user?.role?.name !== 'SUPER_ADMIN'}
      />
      {errors.name && <p className="mt-1 text-sm text-red-500">Name is required</p>}
      {errors.duplicateName && (
        <p className="mt-1 text-sm text-red-500">Company name already exists</p>
      )}

      <Label className="mt-4 mb-1 text-lg font-semibold">Country</Label>
      <CustomSelect
        data={countries}
        value={countryId}
        onChange={(val) => {
          setCountryId(val);
          if (errors.country && val) {
            setErrors((prev) => ({ ...prev, country: false }));
          }
        }}
        placeholder="Select a country"
        error={errors.country}
        errorMessage="Please select a country"
      />

      <Label className="mt-4 mb-1 text-lg font-semibold">Logo (optional)</Label>
      <LogoUpload file={logoFile} onFileSelect={setLogoFile} />

      <div className="flex justify-center gap-2 mb-3 mt-9 sm:justify-end">
        <Button variant="secondary" onClick={onClose} className="px-7 sm:px-10">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          className="px-7 sm:px-10"
          disabled={isEdit ? !isEdited : false}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
};
